<script setup lang="ts">
  import { IconGroup } from '@gat-ship/icons'
  import type { AgencyGroup } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { inject } from 'vue'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyStructureProvider = inject(agencyStructureKey)

  const addAgencyGroupSchema = z.object({
    agencyGroupId: z.string().min(1),
    parentGroupId: z.string().nullable(),
    // fallow-ignore-next-line code-duplication
    name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
    shortName: z
      .string()
      .min(1, 'Short Name is required')
      .max(120, 'Max 120 characters')
  })
  type AddAgencyGroupFormValues = z.infer<typeof addAgencyGroupSchema>

  const form = useForm({
    defaultValues: {
      agencyGroupId: '',
      parentGroupId: null,
      name: '',
      shortName: ''
    } as AddAgencyGroupFormValues,
    validators: {
      onChange: addAgencyGroupSchema
    },
    onSubmit: async ({ value }) => {
      await agencyStructureProvider?.commands.addAgencyGroup({
        agencyGroupId: value.agencyGroupId,
        parentGroupId: value.parentGroupId,
        name: value.name,
        shortName: value.shortName
      })
      isOpen.value = false
    }
  })

  export interface ShowAddAgencyGroupModalParams {
    parentGroupId: string
    agencyGroup?: Omit<AgencyGroup, 'id'>
  }

  function showModal({
    parentGroupId,
    agencyGroup
  }: ShowAddAgencyGroupModalParams) {
    form.reset({
      agencyGroupId: globalUtils.functions.uuid(),
      parentGroupId,
      name: agencyGroup?.name ?? '',
      shortName: agencyGroup?.shortName ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconGroup class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Add Agency Group
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        This will create a new agency group. It can be used to group agencies
        together.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.agency.modal.addAgencyGroup.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="shortName" label="Short Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.agency.modal.addAgencyGroup.shortnameInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Add Group" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
